/**
 * wishlist.js — PixelLifestyle Wishlist
 *
 * Components:
 *  - WishlistStore      — shared localStorage store, cross-tab sync
 *  - <wishlist-toggle>  — per-product heart/toggle button
 *  - <wishlist-count>   — header bubble showing total count
 *  - <wishlist-drawer>  — slide-in drawer with product cards
 *  - <wishlist-page>    — full-page product grid
 */

/* ─────────────────────────────────────────────────
   Utilities
───────────────────────────────────────────────── */

/**
 * HTML-escape a string so it is safe to inject into innerHTML.
 * @param {string} str
 * @returns {string}
 */
function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Format a Shopify price (in cents) to a localised currency string.
 * Falls back to EUR when window.Shopify.currency.active is not set.
 * @param {number} cents
 * @returns {string}
 */
function formatPrice(cents) {
  const currency = window.Shopify?.currency?.active ?? 'EUR';
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
  }).format(cents / 100);
}

function getWishlistPageMarkup() {
  return `
    <wishlist-page>
      <div class="wishlist-page__header">
        <h1 class="wishlist-page__title">
          Wishlist
          <span class="wishlist-page__count"></span>
        </h1>
      </div>

      <div class="wishlist-page__grid"></div>

      <div class="wishlist-page__empty" hidden>
        <p class="wishlist-page__empty-text">Your wishlist is empty</p>
        <a href="/collections/all" class="wishlist-page__empty-cta button">Start Shopping</a>
      </div>
    </wishlist-page>
  `;
}

const REMOVE_ICON_MARKUP = `
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
`;

/* ─────────────────────────────────────────────────
   1. WishlistStore
───────────────────────────────────────────────── */

const WishlistStore = (() => {
  const STORAGE_KEY = 'pixel-wishlist';
  const EVENT_NAME = 'wishlist:update';

  /** @returns {string[]} */
  function getHandles() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  }

  /** @param {string[]} handles */
  function _save(handles) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
    document.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { handles } }));
  }

  /** @param {string} handle */
  function has(handle) {
    return getHandles().includes(handle);
  }

  /**
   * Toggle a handle in/out of the wishlist.
   * @param {string} handle
   * @returns {boolean} — true if the handle is now in the wishlist
   */
  function toggle(handle) {
    const handles = getHandles();
    const idx = handles.indexOf(handle);
    if (idx === -1) {
      handles.push(handle);
    } else {
      handles.splice(idx, 1);
    }
    _save(handles);
    return idx === -1; // true = added
  }

  /** @param {string} handle */
  function remove(handle) {
    const handles = getHandles().filter((h) => h !== handle);
    _save(handles);
  }

  /** @returns {number} */
  function count() {
    return getHandles().length;
  }

  // Sync across browser tabs via the storage event
  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY) {
      document.dispatchEvent(
        new CustomEvent(EVENT_NAME, {
          detail: { handles: getHandles() },
        })
      );
    }
  });

  return { getHandles, _save, has, toggle, remove, count };
})();

/* ─────────────────────────────────────────────────
   2. <wishlist-toggle>
───────────────────────────────────────────────── */

class WishlistToggle extends HTMLElement {
  /** @type {AbortController | null} */
  #ac = null;

  get handle() {
    return this.getAttribute('product-handle') ?? '';
  }

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;

    this.addEventListener('click', this.#handleClick, { signal });
    document.addEventListener('wishlist:update', this.#syncState, { signal });

    this.#syncState();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#ac = null;
  }

  #handleClick = () => {
    const handle = this.handle;
    if (!handle) return;

    WishlistStore.toggle(handle);

    // Bounce animation
    this.classList.remove('wishlist-toggle--bounce');
    // Force reflow so the class removal is committed before re-adding
    void this.offsetWidth;
    this.classList.add('wishlist-toggle--bounce');
    this.addEventListener(
      'animationend',
      () => this.classList.remove('wishlist-toggle--bounce'),
      { once: true }
    );
  };

  #syncState = () => {
    const active = WishlistStore.has(this.handle);

    this.classList.toggle('wishlist-toggle--active', active);

    const btn = this.querySelector('.wishlist-toggle__button');
    if (btn) btn.setAttribute('aria-pressed', String(active));

    const label = this.querySelector('.wishlist-toggle__label');
    if (label) {
      label.textContent = active ? 'In Wishlist' : 'Add to Wishlist';
    }
  };
}

if (!customElements.get('wishlist-toggle')) {
  customElements.define('wishlist-toggle', WishlistToggle);
}

/* ─────────────────────────────────────────────────
   3. <wishlist-count>
───────────────────────────────────────────────── */

class WishlistCount extends HTMLElement {
  /** @type {AbortController | null} */
  #ac = null;

  connectedCallback() {
    this.#ac = new AbortController();
    document.addEventListener('wishlist:update', this.#render, {
      signal: this.#ac.signal,
    });
    this.#render();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#ac = null;
  }

  #render = () => {
    const bubble = this.querySelector('.wishlist-count__bubble');
    if (!bubble) return;

    const n = WishlistStore.count();
    const hidden = n === 0;

    bubble.textContent = String(n);
    bubble.toggleAttribute('hidden', hidden);
    bubble.setAttribute('aria-hidden', String(hidden));

    if (!hidden) {
      bubble.classList.remove('wishlist-count__bubble--pulse');
      void bubble.offsetWidth;
      bubble.classList.add('wishlist-count__bubble--pulse');
      bubble.addEventListener(
        'animationend',
        () => bubble.classList.remove('wishlist-count__bubble--pulse'),
        { once: true }
      );
    }
  };
}

if (!customElements.get('wishlist-count')) {
  customElements.define('wishlist-count', WishlistCount);
}

/* ─────────────────────────────────────────────────
   Shared product-fetching helper
───────────────────────────────────────────────── */

/**
 * Fetch product data for a handle via the Shopify storefront JSON endpoint.
 * Returns null on any non-404 error, removes the stale handle on 404.
 *
 * @param {string} handle
 * @returns {Promise<object|null>}
 */
async function fetchProduct(handle) {
  try {
    const res = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
      headers: { Accept: 'application/json' },
    });
    if (res.status === 404) {
      WishlistStore.remove(handle);
      return null;
    }
    if (!res.ok) return null;
    return await res.json();
  } catch (_) {
    return null;
  }
}

/**
 * Fetch up to `limit` products for the given handles.
 * Stale 404 handles are auto-removed by fetchProduct().
 *
 * @param {string[]} handles
 * @param {number} [limit=Infinity]
 * @returns {Promise<Array<{handle: string, product: object}>>}
 */
async function fetchProducts(handles, limit = Infinity) {
  const slice = handles.slice(0, limit);
  const results = await Promise.all(
    slice.map(async (handle) => {
      const product = await fetchProduct(handle);
      return product ? { handle, product } : null;
    })
  );
  return results.filter(Boolean);
}

/* ─────────────────────────────────────────────────
   4. <wishlist-drawer>
───────────────────────────────────────────────── */

const DRAWER_MAX_ITEMS = 50;

class WishlistDrawer extends HTMLElement {
  /** @type {AbortController | null} */
  #ac = null;

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.querySelector('dialog');
  }

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;

    // Any [data-wishlist-open] element anywhere on the page can open this drawer
    document.addEventListener('click', this.#handleGlobalClick, { signal });

    // Internal close button
    this.addEventListener('click', this.#handleInternalClick, { signal });

    // Keep content fresh when wishlist changes
    document.addEventListener('wishlist:update', this.#handleWishlistUpdate, { signal });

    // Close on backdrop click (clicks on <dialog> itself, outside the panel)
    const dialog = this.#dialog;
    if (dialog) {
      dialog.addEventListener('click', this.#handleBackdropClick, { signal });
      dialog.addEventListener('cancel', () => {
        document.body.style.overflow = '';
      }, { signal });
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#ac = null;
  }

  /* ── Open / Close ── */

  #open() {
    const dialog = this.#dialog;
    if (!dialog || dialog.open) return;
    this.#render(); // kick off async render, don't await
    dialog.showModal();
    document.body.style.overflow = 'hidden';
  }

  #close() {
    const dialog = this.#dialog;
    if (!dialog || !dialog.open) return;
    dialog.close();
    document.body.style.overflow = '';
  }

  /* ── Event handlers ── */

  #handleGlobalClick = (event) => {
    const trigger = event.target.closest('[data-wishlist-open]');
    if (!trigger) return;
    event.preventDefault();
    this.#open();
  };

  #handleInternalClick = (event) => {
    // Close button
    if (event.target.closest('[data-wishlist-close]')) {
      this.#close();
      return;
    }

    // Remove item button
    const removeBtn = event.target.closest('[data-wishlist-remove]');
    if (removeBtn) {
      const handle = removeBtn.dataset.wishlistRemove;
      if (handle) WishlistStore.remove(handle);
    }
  };

  #handleBackdropClick = (event) => {
    const dialog = this.#dialog;
    if (!dialog) return;
    // The dialog element fills the viewport; a click ON it (not its children) is a backdrop click
    if (event.target === dialog) {
      this.#close();
    }
  };

  #handleWishlistUpdate = () => {
    const dialog = this.#dialog;
    if (dialog?.open) {
      this.#render();
    }
  };

  /* ── Rendering ── */

  async #render() {
    const list = this.querySelector('.wishlist-drawer__items');
    const empty = this.querySelector('.wishlist-drawer__empty');
    if (!list) return;

    const handles = WishlistStore.getHandles();

    if (handles.length === 0) {
      list.innerHTML = '';
      if (empty) empty.hidden = false;
      const footer = this.querySelector('.wishlist-drawer__footer');
      if (footer) footer.hidden = true;
      const countEl = this.querySelector('.wishlist-drawer__count');
      if (countEl) countEl.textContent = '';
      return;
    }

    if (empty) empty.hidden = true;

    // Fetch up to MAX+1 so we know whether there are more
    const items = await fetchProducts(handles, DRAWER_MAX_ITEMS + 1);
    const displayItems = items.slice(0, DRAWER_MAX_ITEMS);
    const totalCount = handles.length; // use original length for "view all" label

    list.innerHTML = displayItems
      .map(({ handle, product }) => this.#renderItem(handle, product))
      .join('');

    // Update drawer count badge
    const countEl = this.querySelector('.wishlist-drawer__count');
    if (countEl) countEl.textContent = `(${totalCount})`;

    // Show/hide the static "View All" footer
    const footer = this.querySelector('.wishlist-drawer__footer');
    if (footer) footer.hidden = handles.length === 0;
  }

  /**
   * @param {string} handle
   * @param {object} product
   * @returns {string}
   */
  #renderItem(handle, product) {
    const image = product.images?.[0]
      ? `<img class="wishlist-drawer__item-image" src="${escapeHtml(product.images[0])}" alt="${escapeHtml(product.title)}" width="80" height="80" loading="lazy">`
      : '';
    const price = formatPrice(product.price ?? 0);

    return `
      <div class="wishlist-drawer__item" data-handle="${escapeHtml(handle)}">
        <a href="/products/${escapeHtml(handle)}" class="wishlist-drawer__item-image-wrap">
          ${image}
        </a>
        <div class="wishlist-drawer__item-info">
          <a href="/products/${escapeHtml(handle)}" class="wishlist-drawer__item-title">
            ${escapeHtml(product.title)}
          </a>
          <span class="wishlist-drawer__item-price">${escapeHtml(price)}</span>
        </div>
        <button
          class="wishlist-drawer__item-remove"
          data-wishlist-remove="${escapeHtml(handle)}"
          aria-label="Remove ${escapeHtml(product.title)} from wishlist"
          type="button"
        >${REMOVE_ICON_MARKUP}</button>
      </div>
    `;
  }
}

if (!customElements.get('wishlist-drawer')) {
  customElements.define('wishlist-drawer', WishlistDrawer);
}

/* ─────────────────────────────────────────────────
   5. <wishlist-page>
───────────────────────────────────────────────── */

class WishlistPage extends HTMLElement {
  /** @type {AbortController | null} */
  #ac = null;

  connectedCallback() {
    this.#ac = new AbortController();
    document.addEventListener('wishlist:update', this.#render, {
      signal: this.#ac.signal,
    });
    this.#render();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#ac = null;
  }

  /** Delegated click handler for remove buttons. */
  #handleClick = (event) => {
    const btn = event.target.closest('[data-wishlist-remove]');
    if (!btn) return;
    const handle = btn.dataset.wishlistRemove;
    if (handle) WishlistStore.remove(handle);
  };

  #render = async () => {
    // Attach click handler each render cycle (delegated, so one is fine)
    this.removeEventListener('click', this.#handleClick);
    this.addEventListener('click', this.#handleClick);

    const grid = this.querySelector('.wishlist-page__grid');
    const empty = this.querySelector('.wishlist-page__empty');
    const countEl = this.querySelector('.wishlist-page__count');

    const handles = WishlistStore.getHandles();

    if (countEl) {
      countEl.textContent = String(handles.length);
    }

    if (handles.length === 0) {
      if (grid) grid.innerHTML = '';
      if (empty) empty.hidden = false;
      return;
    }

    if (empty) empty.hidden = true;

    const items = await fetchProducts(handles);

    if (grid) {
      grid.innerHTML = items
        .map(({ handle, product }) => this.#renderCard(handle, product))
        .join('');
    }
  };

  /**
   * @param {string} handle
   * @param {object} product
   * @returns {string}
   */
  #renderCard(handle, product) {
    const image = product.images?.[0]
      ? `<img class="wishlist-page__card-image" src="${escapeHtml(product.images[0])}" alt="${escapeHtml(product.title)}" width="400" height="500" loading="lazy">`
      : '';
    const price = formatPrice(product.price ?? 0);

    return `
      <div class="wishlist-page__card" data-handle="${escapeHtml(handle)}">
        <a href="/products/${escapeHtml(handle)}" class="wishlist-page__card-image-wrap">
          ${image}
        </a>
        <div class="wishlist-page__card-body">
          <a href="/products/${escapeHtml(handle)}" class="wishlist-page__card-title">
            ${escapeHtml(product.title)}
          </a>
          <span class="wishlist-page__card-price">${escapeHtml(price)}</span>
          <button
            class="wishlist-page__card-remove"
            data-wishlist-remove="${escapeHtml(handle)}"
            aria-label="Remove ${escapeHtml(product.title)} from wishlist"
            type="button"
          >${REMOVE_ICON_MARKUP}</button>
        </div>
      </div>
    `;
  }
}

if (!customElements.get('wishlist-page')) {
  customElements.define('wishlist-page', WishlistPage);
}

function ensureWishlistPageRoute() {
  const path = window.location.pathname.replace(/\/+$/, '') || '/';

  if (path !== '/pages/wishlist') return;
  if (document.querySelector('wishlist-page')) return;

  const container =
    document.querySelector('main .section-content-wrapper') ??
    document.querySelector('main .page-width') ??
    document.querySelector('main');

  if (!container) return;

  container.innerHTML = getWishlistPageMarkup();
}

ensureWishlistPageRoute();
